import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import AppSheetMrtTable from './AppSheetMrtTable';

const DEFAULT_PAGE_SIZE = 100;

const MetricCard = ({ label, value }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </CardContent>
  </Card>
);

const buildColumns = (fields = []) => {
  const base = [
    { id: '__accountName', header: 'Account Name', size: 200, accessorFn: (row) => row.__accountName },
    { id: '__accountId', header: 'Account Id', size: 180, accessorFn: (row) => row.__accountId },
    { id: '__accountStatus', header: 'Account Status', size: 160, accessorFn: (row) => row.__accountStatus },
    { id: '__hasTradeLineItems', header: 'Trade Line Items', size: 160, accessorFn: (row) => row.__hasTradeLineItems },
    { id: '__bacCode', header: 'BAC_Code__c', size: 140, accessorFn: (row) => row.__bacCode },
  ];
  const extra = fields.map((field) => ({
    id: field.name,
    header: field.displayName || field.name,
    size: 180,
    accessorFn: (row) => row?.[field.name],
  }));
  return [...base, ...extra];
};

const AppSheetAccountsMissingBac = () => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });

  const { data, isLoading, error } = useQuery({
    queryKey: ['appsheet-accounts-missing-bac', pagination],
    queryFn: async () => {
      const params = {
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      };
      const response = await axios.get('/api/appsheet/reports/accounts-missing-bac', { params });
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, err) => {
      const status = err?.response?.status;
      if (status === 429) return false;
      return failureCount < 2;
    },
  });

  const columns = useMemo(() => buildColumns(data?.table?.fields || []), [data?.table?.fields]);
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const total = data?.total ?? 0;
  const activeCount = data?.summary?.activeCount ?? 0;
  const tradeCount = data?.summary?.tradeCount ?? 0;
  const tradeLineItemsTotal = data?.summary?.tradeLineItemsTotal ?? 0;
  const tradeLineItemsLivePending = data?.summary?.tradeLineItemsLivePending ?? 0;
  const tradeAccountsLivePending = data?.summary?.tradeAccountsLivePending ?? 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Accounts Missing BAC
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.response?.data?.message || error?.message || 'Failed to load accounts missing BAC report.'}
        </Alert>
      )}

      {!isLoading && !error && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Total Missing BAC" value={total} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Active Accounts" value={activeCount} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Missing BAC with Trade Lines" value={tradeCount} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Trade Line Items (total)" value={tradeLineItemsTotal} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Trade Line Items (live/pending)" value={tradeLineItemsLivePending} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <MetricCard label="Trade Accounts (live/pending)" value={tradeAccountsLivePending} />
            </Grid>
          </Grid>

          <AppSheetMrtTable
            rows={rows}
            columns={columns}
            rowCount={total}
            isLoading={isLoading}
            pagination={pagination}
            onPaginationChange={setPagination}
            title="Accounts Missing BAC"
          />
        </>
      )}
    </Box>
  );
};

export default AppSheetAccountsMissingBac;
